import { api } from './api';
import { dedupedFetch, invalidateCache } from './cache';
import type {
  AvaliacaoEtapa2,
  AvaliacaoEtapa2Input,
} from 'types/etapa2';

const COLLECTION = 'avaliacao_etapa2__c';
const COLLECTION_PATH = `/database/${COLLECTION}`;

const minhasAvaliacoesKey = (avaliadorId: string) =>
  `avaliacoes-etapa2-${avaliadorId}`;

const avaliacaoAtivaKey = (avaliadorId: string, category: string) =>
  `avaliacao-ativa-etapa2-${avaliadorId}-${category}`;

export const fetchMinhasAvaliacoesEtapa2 = async (
  // avaliadorId é derivado do token do player server-side (context.player);
  // mantido aqui apenas para escopo de cache.
  avaliadorId: string
): Promise<AvaliacaoEtapa2[]> => {
  return dedupedFetch(
    minhasAvaliacoesKey(avaliadorId),
    async () => {
      const { data } = await api.post('/find/avaliacoes_etapa2_by_avaliador', {});
      return Array.isArray(data) ? (data as AvaliacaoEtapa2[]) : [];
    },
    30_000
  );
};

const fetchAvaliacaoAtivaEtapa2Raw = async (
  // avaliadorId é derivado do token do player server-side (context.player);
  // mantido aqui apenas para escopo de cache via fetchAvaliacaoAtivaEtapa2.
  avaliadorId: string,
  category: string
): Promise<AvaliacaoEtapa2 | null> => {
  const { data } = await api.post('/find/avaliacao_etapa2_ativa', { category });
  const results = Array.isArray(data) ? data : [];
  return results[0] ?? null;
};

export const fetchAvaliacaoAtivaEtapa2 = async (
  avaliadorId: string,
  category: string
): Promise<AvaliacaoEtapa2 | null> => {
  return dedupedFetch(
    avaliacaoAtivaKey(avaliadorId, category),
    () => fetchAvaliacaoAtivaEtapa2Raw(avaliadorId, category),
    30_000
  );
};

const fetchAvaliacaoById = async (
  id: string
): Promise<AvaliacaoEtapa2 | null> => {
  const { data } = await api.post('/find/avaliacao_etapa2_by_id', { id });
  const results = Array.isArray(data) ? data : [];
  return results[0] ?? null;
};

export const markAvaliacaoSuperseded = async (id: string): Promise<void> => {
  const existing = await fetchAvaliacaoById(id);
  if (!existing) return;
  await api.put(COLLECTION_PATH, {
    ...existing,
    supersededAt: Date.now(),
  });
};

export const submitAvaliacaoEtapa2 = async (
  payload: AvaliacaoEtapa2Input
): Promise<AvaliacaoEtapa2> => {
  const previous = await fetchAvaliacaoAtivaEtapa2Raw(
    payload.avaliadorId,
    payload.category
  );

  if (previous) {
    await markAvaliacaoSuperseded(previous._id);
  }

  const { data } = await api.post(COLLECTION_PATH, payload);

  invalidarCacheAvaliacoesEtapa2(payload.avaliadorId, payload.category);

  return data as AvaliacaoEtapa2;
};

export const invalidarCacheAvaliacoesEtapa2 = (
  avaliadorId: string,
  category?: string
): void => {
  invalidateCache(minhasAvaliacoesKey(avaliadorId));
  if (category) {
    invalidateCache(avaliacaoAtivaKey(avaliadorId, category));
  }
};
